import { useSelector } from 'react-redux'

import FeatureToggle from '~/constants/featureToggle'
import { getFeatureToggle } from '~/store/reducers/constants'
import { getMemberIsLoading } from '~/store/reducers/members'

/**
 * Hook to determine if license entry is needed (for disabling buttons/actions).
 * Combines checks for missing DVM license and state license mismatch.
 * When the warning is visible, the button should be disabled.
 *
 * @param isDvmLicenseRequired - Whether a DVM license is required for this prescription
 * @param currentDoctorHasDvmLicense - Whether the current doctor has a DVM license (null = no doctor/loading)
 * @param currentDoctorHasMatchingStateLicense - Whether the doctor has a matching state license (null = no doctor/loading)
 * @returns true if license entry is needed (disables approve button)
 */
export const useNeedsLicenseEntry = (
  isDvmLicenseRequired: boolean,
  currentDoctorHasDvmLicense: boolean | null,
  currentDoctorHasMatchingStateLicense: boolean | null,
): boolean => {
  const memberIsLoading = useSelector(getMemberIsLoading)
  const isLicenseStateValidationEnabled = useSelector(
    getFeatureToggle(FeatureToggle.FE_DVM_LICENSE_STATE_VALIDATION),
  )

  // Check if DVM license warning should be visible
  const isDvmLicenseWarningVisible =
    isDvmLicenseRequired &&
    currentDoctorHasDvmLicense === false &&
    !memberIsLoading

  // Check if state license warning should be visible
  const isStateLicenseWarningVisible =
    isLicenseStateValidationEnabled &&
    isDvmLicenseRequired &&
    currentDoctorHasMatchingStateLicense !== null &&
    !currentDoctorHasMatchingStateLicense &&
    !memberIsLoading

  // Button is disabled when either warning is visible
  return isDvmLicenseWarningVisible || isStateLicenseWarningVisible
}
